/**
 * Эфир своим звуком: состояние и формат кадра.
 *
 * ЧТО ЭТО. Модератор выбирает звуковой вход своей машины — микрофон,
 * виртуальный кабель, что угодно из списка устройств, — и то, что
 * там играет, идёт в эфир. Панель кодирует поток в Opus и шлёт
 * двоичными кадрами через hub, оверлей их декодирует и подмешивает
 * в свой граф Web Audio.
 *
 * ПОЧЕМУ РАЗБОР ОБЩИЙ. Оверлей по этому сообщению настраивает
 * декодер, панель — рисует, кто в эфире. Две реализации разошлись бы
 * молча и именно там, где это дорого: панель показывала бы «эфир
 * идёт», а зритель слышал тишину.
 *
 * ПОЧЕМУ ЗАГОЛОВОК КАДРА ТОЖЕ ЗДЕСЬ. Его пишет панель, а читает
 * оверлей — то есть это ровно та пара, которая обязана понимать
 * друг друга байт в байт. Разъедься они на один байт смещения —
 * декодер получил бы мусор и замолчал бы без единой ошибки в консоли.
 */
/** Как закодирован поток. */
import { decodeMessage } from './message.js';
/** Эфира нет: обычное состояние канала. */
export const AIR_OFF = { on: false };
/**
 * Частоты и длительности кадра, которые понимает Opus.
 *
 * Списки закрытые и совпадают с теми, что стережёт hub. Значение
 * не из списка — это не тихий звук, а отказ настроить декодер:
 * исключение посреди эфира у зрителя, а не у того, кто его прислал.
 */
const RATES = new Set([8000, 12000, 16000, 24000, 48000]);
const FRAMES = new Set([10, 20, 40, 60]);
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
/** Разбирает настройки потока. Возвращает null, если им нельзя верить. */
function parseConfig(raw) {
    if (!isRecord(raw))
        return null;
    const { sampleRate, channels, frameMs, profile } = raw;
    if (typeof sampleRate !== 'number' || !RATES.has(sampleRate))
        return null;
    if (typeof frameMs !== 'number' || !FRAMES.has(frameMs))
        return null;
    if (channels !== 1 && channels !== 2)
        return null;
    if (profile !== 'voice' && profile !== 'sound')
        return null;
    return { sampleRate, channels, frameMs, profile };
}
/**
 * Разбирает сообщение `air`.
 *
 * Возвращает null для всего остального: вызывающий по этому понимает,
 * что сообщение не про эфир, и передаёт дальше.
 *
 * ЭФИР БЕЗ ГОДНЫХ НАСТРОЕК — ЭТО ВЫКЛЮЧЕННЫЙ ЭФИР, а не ошибка
 * разбора. Оверлею важно не «сообщение странное», а «играть нечего»:
 * второе он умеет, первое ему делать нечем.
 */
export function parseAir(raw) {
    const parsed = decodeMessage(raw);
    if (parsed === null)
        return null;
    if (!isRecord(parsed) || parsed.t !== 'air')
        return null;
    if (parsed.on !== true)
        return AIR_OFF;
    const config = parseConfig(parsed.config);
    if (config === null)
        return AIR_OFF;
    const holder = isRecord(parsed.holder) ? parsed.holder : {};
    return {
        on: true,
        config,
        holder: {
            clientId: typeof holder.clientId === 'string' ? holder.clientId : '',
            ...(typeof holder.userId === 'string' ? { userId: holder.userId } : {}),
            ...(typeof holder.userName === 'string' ? { userName: holder.userName } : {}),
        },
    };
}
// ─── Кадр ─────────────────────────────────────────────────────────
/**
 * Версия формата кадра.
 *
 * Нужна не «на будущее», а прямо сейчас: панель и оверлей
 * выкатываются порознь, и оверлей, оставшийся в OBS со вчерашней
 * сборкой, обязан молча выбросить непонятный кадр, а не скормить
 * его декодеру.
 */
export const AIR_VERSION = 1;
/** Версия, признаки и номер кадра. */
export const AIR_HEADER_BYTES = 6;
/**
 * Собирает кадр для отправки.
 *
 * Времени в заголовке нет намеренно: длительность кадра постоянна
 * и объявлена в настройках, поэтому время считается из номера.
 * Второе поле с тем же смыслом однажды разошлось бы с первым.
 */
export function encodeAirFrame(seq, payload) {
    const out = new Uint8Array(AIR_HEADER_BYTES + payload.length);
    const view = new DataView(out.buffer);
    out[0] = AIR_VERSION;
    out[1] = 0;
    // Номер по модулю: полсотни кадров в секунду переполняют uint32
    // почти через три года непрерывного эфира, но обрыв счёта должен
    // быть предсказуемым, а не зависеть от того, как ведёт себя
    // DataView с числом больше 2^32
    view.setUint32(2, seq >>> 0);
    out.set(payload, AIR_HEADER_BYTES);
    return out;
}
/**
 * Разбирает пришедший кадр.
 *
 * Возвращает null для чужой версии и для обрезка: и то и другое
 * значит «этот кадр не наш», и единственное, что с ним можно
 * сделать, — выбросить. Декодер, накормленный мусором, замолкает
 * без единой ошибки в консоли.
 */
export function decodeAirFrame(data) {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    if (bytes.length <= AIR_HEADER_BYTES)
        return null;
    if (bytes[0] !== AIR_VERSION)
        return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return { seq: view.getUint32(2), payload: bytes.subarray(AIR_HEADER_BYTES) };
}
//# sourceMappingURL=air.js.map